import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(ChartDataLabels);

// Connects to data-controller="vacan-chart"
export default class extends Controller {
  static targets = [
    "monthlyCanvas",
    "dayOfWeekCanvas",
    "timeCanvas",
    "placeCanvas",
    "stayCanvas"
  ];

  connect() {
    console.log("Vacan Chart controller connected");
    const url = this.data.get("url");
    this.renderChart(url);
  }

  async renderChart(url) {
    const response = await fetch(url, {
      headers: { Accept: "application/json" }
    });
    const data = await response.json();

    // 総数の表示
    const totalCountElement = document.querySelector(
      "h5.my-auto span.text-danger"
    );
    if (totalCountElement) {
      totalCountElement.textContent = data.total_count.toLocaleString();
    }

    const datalabelsOptions = {
      anchor: "end",
      align: "end",
      color: "#333",
      font: { weight: "bold", size: 11 },
      formatter: (value, context) => {
        const label = context.dataset.label || "";
        if (label.includes("％") || label.includes("%")) return value + "%";
        return value.toLocaleString();
      }
    };

    const baseOptions = (title) => ({
      responsive: true,
      plugins: {
        title: { display: true, text: title },
        datalabels: datalabelsOptions,
        legend: { display: false }
      },
      scales: {
        x: { beginAtZero: true },
        y: { beginAtZero: true }
      }
    });

    // --- 月別グラフ ---
    if (this.monthlyChart) this.monthlyChart.destroy();
    this.monthlyChart = new Chart(this.monthlyCanvasTarget, {
      type: "bar",
      data: data.monthly_series,
      options: baseOptions("月別 車両数")
    });

    // --- 曜日別グラフ ---
    if (this.dayOfWeekChart) this.dayOfWeekChart.destroy();
    this.dayOfWeekChart = new Chart(this.dayOfWeekCanvasTarget, {
      type: "bar",
      data: data.day_of_week_series,
      options: baseOptions("曜日別 車両数")
    });

    // --- 時間帯別グラフ ---
    if (this.timeChart) this.timeChart.destroy();
    this.timeChart = new Chart(this.timeCanvasTarget, {
      type: "line",
      data: data.time_series,
      options: {
        ...baseOptions("時間帯別 人・車両"),
        plugins: {
          ...baseOptions().plugins,
          legend: { display: true },
          datalabels: {
            ...datalabelsOptions,
            align: "top"
          }
        }
      }
    });

    // --- 場所別ランキング ---
    if (this.placeChart) this.placeChart.destroy();
    this.placeChart = new Chart(this.placeCanvasTarget, {
      type: "bar",
      data: data.place_ranking_series,
      options: baseOptions("駐車場利用車両の地名別ランキング（上位20）")
    });

    // --- 滞在時間分布 ---
    if (this.stayChart) this.stayChart.destroy();
    this.stayChart = new Chart(this.stayCanvasTarget, {
      type: "bar",
      data: data.stay_duration_series,
      options: {
        plugins: {
          title: { display: true, text: "月別 滞在時間構成比（積み上げ100%）" },
          datalabels: {
            color: "#333",
            font: { weight: "bold", size: 10 },
            formatter: (value) => (value && value > 5 ? value + "%" : "")
          }
        },
        responsive: true,
        scales: {
          x: { stacked: true },
          y: {
            stacked: true,
            min: 0,
            max: 100,
            title: { display: true, text: "割合（％）" }
          }
        }
      }
    });
  }

  update(event) {
    const form = event.target;
    if (event.submitter?.value === "csv") return;

    event.preventDefault();
    let url =
      form.action + "?" + new URLSearchParams(new FormData(form)).toString();
    if (!url.includes("format=json")) url += "&format=json";
    this.renderChart(url);
  }

  async updateMonthly(event) {
    if (event.submitter?.value === "csv") return;
    event.preventDefault();

    const datalabelsOptions = {
      anchor: "end",
      align: "end",
      color: "#333",
      font: { weight: "bold", size: 11 },
      formatter: (value, context) => {
        const label = context.dataset.label || "";
        if (label.includes("％") || label.includes("%")) return value + "%";
        return value.toLocaleString() + "件";
      }
    };

    const baseOptions = (title) => ({
      responsive: true,
      plugins: {
        title: { display: true, text: title },
        datalabels: datalabelsOptions,
        legend: { display: false }
      },
      scales: {
        x: { beginAtZero: true },
        y: { beginAtZero: true }
      }
    });

    const form = event.target;

    const url = new URL(
      `${origin}${form.closest("[data-vacan-chart-url]").dataset.vacanChartUrl}`
    );
    url.search = new URLSearchParams(new FormData(form)).toString();
    url.search += "&format=json";

    const response = await fetch(url, {
      headers: {
        Accept: "application/json"
      }
    });
    const data = await response.json();
    this.monthlyChart.destroy();
    this.monthlyChart = new Chart(this.monthlyCanvasTarget, {
      type: "bar",
      data: data.monthly_series,
      options: baseOptions("月別 車両数")
    });
  }

  async updateDayOfWeek(event) {
    if (event.submitter?.value === "csv") return;
    event.preventDefault();

    const datalabelsOptions = {
      anchor: "end",
      align: "end",
      color: "#333",
      font: { weight: "bold", size: 11 },
      formatter: (value, context) => {
        const label = context.dataset.label || "";
        if (label.includes("％") || label.includes("%")) return value + "%";
        return value.toLocaleString() + "件";
      }
    };

    const baseOptions = (title) => ({
      responsive: true,
      plugins: {
        title: { display: true, text: title },
        datalabels: datalabelsOptions,
        legend: { display: false }
      },
      scales: {
        x: { beginAtZero: true },
        y: { beginAtZero: true }
      }
    });

    const form = event.target;

    const url = new URL(
      `${origin}${form.closest("[data-vacan-chart-url]").dataset.vacanChartUrl}`
    );
    url.search = new URLSearchParams(new FormData(form)).toString();
    url.search += "&format=json";

    const response = await fetch(url, {
      headers: {
        Accept: "application/json"
      }
    });
    const data = await response.json();
    this.dayOfWeekChart.destroy();
    this.dayOfWeekChart = new Chart(this.dayOfWeekCanvasTarget, {
      type: "bar",
      data: data.day_of_week_series,
      options: baseOptions("曜日別 車両数")
    });
  }

  async updateTime(event) {
    if (event.submitter?.value === "csv") return;
    event.preventDefault();

    const form = event.target;

    const url = new URL(
      `${origin}${form.closest("[data-vacan-chart-url]").dataset.vacanChartUrl}`
    );
    url.search = new URLSearchParams(new FormData(form)).toString();
    url.search += "&format=json";

    const response = await fetch(url, {
      headers: {
        Accept: "application/json"
      }
    });
    const data = await response.json();

    // 総数の表示
    const totalCountElement = document.querySelector(
      "h5.my-auto span.text-danger"
    );
    if (totalCountElement) {
      totalCountElement.textContent = data.total_count.toLocaleString();
    }

    const datalabelsOptions = {
      anchor: "end",
      align: "end",
      color: "#333",
      font: { weight: "bold", size: 11 },
      formatter: (value, context) => {
        const label = context.dataset.label || "";
        if (label.includes("％") || label.includes("%")) return value + "%";
        return value.toLocaleString() + "件";
      }
    };

    const baseOptions = (title) => ({
      responsive: true,
      plugins: {
        title: { display: true, text: title },
        datalabels: datalabelsOptions,
        legend: { display: false }
      },
      scales: {
        x: { beginAtZero: true },
        y: { beginAtZero: true }
      }
    });

    this.timeChart.destroy();
    this.timeChart = new Chart(this.timeCanvasTarget, {
      type: "line",
      data: data.time_series,
      options: {
        ...baseOptions("時間帯別 人・車両"),
        plugins: {
          ...baseOptions().plugins,
          legend: { display: true },
          datalabels: {
            ...datalabelsOptions,
            align: "top"
          }
        }
      }
    });
  }

  async updatePlace(event) {
    if (event.submitter?.value === "csv") return;
    event.preventDefault();

    const form = event.target;

    const url = new URL(
      `${origin}${form.closest("[data-vacan-chart-url]").dataset.vacanChartUrl}`
    );
    url.search = new URLSearchParams(new FormData(form)).toString();
    url.search += "&format=json";

    const response = await fetch(url, {
      headers: {
        Accept: "application/json"
      }
    });
    const data = await response.json();
    this.placeChart.destroy();
    this.placeChart = new Chart(this.placeCanvasTarget, {
      type: "bar",
      data: data.place_ranking_series,
      options: {
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: "駐車場利用車両の地名別ランキング（上位20）"
          },
          datalabels: {
            anchor: "end",
            align: "end",
            color: "#333",
            font: { weight: "bold", size: 11 },
            formatter: (value) => value.toLocaleString() + "件"
          },
          legend: { display: false }
        },
        scales: {
          x: { beginAtZero: true },
          y: { beginAtZero: true }
        }
      }
    });
  }
  async updateStay(event) {
    if (event.submitter?.value === "csv") return;
    event.preventDefault();

    const form = event.target;

    const url = new URL(
      `${origin}${form.closest("[data-vacan-chart-url]").dataset.vacanChartUrl}`
    );
    url.search = new URLSearchParams(new FormData(form)).toString();
    url.search += "&format=json";

    const response = await fetch(url, {
      headers: {
        Accept: "application/json"
      }
    });
    const data = await response.json();
    this.stayChart.destroy();
    this.stayChart = new Chart(this.stayCanvasTarget, {
      type: "bar",
      data: data.stay_duration_series,
      options: {
        plugins: {
          title: { display: true, text: "月別 滞在時間構成比（積み上げ100%）" },
          datalabels: {
            color: "#333",
            font: { weight: "bold", size: 10 },
            formatter: (value) => (value && value > 5 ? value + "%" : "")
          }
        },
        responsive: true,
        scales: {
          x: { stacked: true },
          y: {
            stacked: true,
            min: 0,
            max: 100,
            title: { display: true, text: "割合（％）" }
          }
        }
      }
    });
  }
}
